import { makeAutoObservable, runInAction } from "mobx";
import { esgService } from "../../services";
import { showToast } from "@/lib/utils/toast";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";
import { esgReportWorkflowStore } from "./esg-report-workflow.store";
import { esgDisclosureSelectionStore } from "./esg-disclosure-selection.store";
import { esgDataEntryStore } from "./esg-data-entry.store";
import { esgCollaborationStore } from "./esg-collaboration.store";

class ESGReportBuilderStore {
  reportId = null;
  reportName = "";
  reportYear = new Date().getFullYear().toString();
  framework = "";
  orgId = null;
  orgName = "";
  notes = "";

  includeCover = true;
  includeToc = true;
  includeAppendix = true;

  status = "draft";

  createdAt = null;
  updatedAt = null;
  submittedAt = null;

  loading = false;
  error = null;
  isDirty = false;

  constructor() {
    makeAutoObservable(this);
  }

  get isNew() {
    return !this.reportId;
  }

  get canEdit() {
    return this.status === "draft" || this.status === "in_review";
  }

  get metadata() {
    return {
      reportId: this.reportId,
      reportName: this.reportName,
      reportYear: this.reportYear,
      framework: this.framework,
      orgId: this.orgId,
      orgName: this.orgName,
      status: this.status,
    };
  }

  // Actions
  setReportId = (reportId) => {
    this.reportId = reportId;
  };

  setReportName = (name) => {
    if (this.reportName !== name) {
      this.reportName = name;
      this.isDirty = true;
    }
  };

  setReportYear = (year) => {
    if (this.reportYear !== year) {
      this.reportYear = year;
      this.isDirty = true;
    }
  };

  setFramework = (framework) => {
    if (this.framework !== framework) {
      this.framework = framework;
      this.isDirty = true;
    }
  };

  setOrganization = (id, name) => {
    if (this.orgId !== id || this.orgName !== name) {
      this.orgId = id;
      this.orgName = name;
      this.isDirty = true;
    }
  };

  setIncludeCover = (value) => {
    if (this.includeCover !== value) {
      this.includeCover = value;
      this.isDirty = true;
    }
  };

  setIncludeToc = (value) => {
    if (this.includeToc !== value) {
      this.includeToc = value;
      this.isDirty = true;
    }
  };

  setNotes = (value) => {
    if (this.notes !== value) {
      this.notes = value;
      this.isDirty = true;
    }
  };

  createReport = async () => {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      const payload = this.buildReportPayload();
      const createPromise = esgService.createReport(payload);

      showToast.promise(createPromise, {
        loading: "Saving Progress",
        error: (err) => err.message,
        success: "Successfully saved progress",
      });

      const data = await promiseWithMinDelay(createPromise);

      if (data) {
        runInAction(() => {
          this.reportId = data.report._id;
          this.status = data.report.status;
          this.createdAt = data.report.createdAt;
          this.isDirty = false;
          esgDisclosureSelectionStore.isDirty = false;
          esgDataEntryStore.isDirty = false;
        });
        return data;
      }
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  updateReport = async () => {
    if (this.isNew) return;

    runInAction(() => {
      this.loading = true;
    });

    try {
      const payload = this.buildReportPayload();
      const updatePromise = esgService.updateReport(this.reportId, payload);

      showToast.promise(updatePromise, {
        loading: "Saving Progress",
        error: (err) => err.message,
        success: "Successfully saved progress",
      });

      const data = await promiseWithMinDelay(updatePromise);

      if (data) {
        runInAction(() => {
          this.updatedAt = data.report.updatedAt;
          this.isDirty = false;
          esgDisclosureSelectionStore.isDirty = false;
          esgDataEntryStore.isDirty = false;
        });
        return data;
      }
    } catch (error) {
      runInAction(() => {
        this.error = error.message;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
        this.error = null;
      });
    }
  };

  loadReport = async (reportId) => {
    runInAction(() => {
      this.loading = true;
    });

    try {
      const { report } = await esgService.getReport(reportId);

      console.log("reportName", report.reportName);

      if (report) {
        runInAction(() => {
          this.reportId = report._id;
          this.reportName = report.reportName;
          this.reportYear = report.reportYear?.toString();
          this.framework = report.framework;
          this.orgId = report.orgId;
          this.orgName = report.orgName;
          this.notes = report.notes;
          this.includeCover = report.includeCover;
          this.includeToc = report.includeToc;
          this.status = report.status;
          this.createdAt = report.createdAt;
          this.updatedAt = report.updatedAt;
          this.submittedAt = report.submittedAt;
          this.isDirty = false;

          esgReportWorkflowStore.goToStep(report.currentStep);
          esgDisclosureSelectionStore.loadSelections(report);
          esgDataEntryStore.loadData(report);
          esgCollaborationStore.loadData(report);
        });
      }
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  buildReportPayload = () => {
    return {
      orgId: this.orgId,
      orgName: this.orgName,
      reportName: this.reportName,
      reportYear: this.reportYear,
      framework: this.framework,
      notes: this.notes,
      includeCover: this.includeCover,
      includeToc: this.includeToc,
      currentStep: esgReportWorkflowStore.currentStep,
      selectedDisclosures: esgDisclosureSelectionStore.selectedCodes,
      customDisclosures: esgDisclosureSelectionStore.customDisclosures,
      disclosureData: esgDataEntryStore.serializeDisclosureData(),
      sustainabilityStrategy: {
        overview: "",
        environmental: [],
        social: [],
        governance: [],
      },
    };
  };

  resetBuilder = () => {
    runInAction(() => {
      this.reportId = null;
      this.reportName = "";
      this.reportYear = new Date().getFullYear().toString();
      this.framework = "";
      this.orgId = null;
      this.orgName = "";
      this.notes = "";
      this.includeCover = true;
      this.includeToc = true;
      this.includeAppendix = true;
      this.status = "draft";
      this.error = null;
      this.isDirty = false;
    });
  };
}

export const esgReportBuilderStore = new ESGReportBuilderStore();
